import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Loader } from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import { useUsage } from "../../hooks/useUsage";
import { api } from "../../utils/apiClient";
import AuditErrorDialog from "../AuditErrorDialog";
import AuditLoaderDialog from "../AuditLoaderDialog";
import LimitReachedDialog from "../LimitReachedDialog";

const AuditInput = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [url, setUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<string | null>(null);
  const [limitMessage, setLimitMessage] = useState<string | null>(null);
  const { refresh: refreshUsage } = useUsage();

  const openAuth = () => navigate("?auth=login");

  const startAudit = async () => {
    if (!user) {
      openAuth();
      return;
    }
    setError(null);
    setErrorType(null);
    setLimitMessage(null);
    setIsSubmitting(true);

    try {
      const response = await api.post("/audit/start", { url });
      const resultData = response.data;

      if (resultData?.status === "failed") {
        throw new Error(resultData.error || "An error occurred during the audit.");
      }

      window.dispatchEvent(new CustomEvent("authChange"));
      navigate(`/audit/report/${resultData.audit_id}`, {
        state: { auditResponse: resultData },
      });
    } catch (e) {
      const err = e as {
        response?: { status?: number; data?: { detail?: string } };
        message?: string;
      };
      if (err.response?.status === 403) {
        refreshUsage();
        setLimitMessage(err.response?.data?.detail || "You've reached your plan limit.");
        setIsSubmitting(false);
        return;
      }
      let errorMessage = err.response?.data?.detail || err.message || "Failed to start audit.";

      const prefix = "An unexpected error occurred during the audit:";
      if (errorMessage.includes(prefix)) {
        errorMessage = errorMessage.replace(prefix, "").trim();
      }
      errorMessage = errorMessage.replace(/^\d{3}:\s*/, "");

      const isDnsError =
        errorMessage.includes("DNS resolution failed") ||
        errorMessage.includes("URL requested might not be correct") ||
        errorMessage.includes("domain does not exist");

      const isBlockedError =
        errorMessage.includes("Website inaccessible") ||
        errorMessage.includes("No meaningful content") ||
        errorMessage.includes("Could not fetch homepage");

      setError(errorMessage);
      if (isDnsError) {
        setErrorType("dns");
      } else if (isBlockedError) {
        setErrorType("blocked");
      } else {
        setErrorType("general");
      }
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    startAudit();
  };

  return (
    <>
      {isSubmitting && <AuditLoaderDialog url={url} />}

      {error && (errorType === "general" || errorType === "blocked") && !isSubmitting && (
        <AuditErrorDialog onClose={() => setError(null)} onRetry={startAudit} url={url} />
      )}

      {limitMessage && !isSubmitting && (
        <LimitReachedDialog message={limitMessage} onClose={() => setLimitMessage(null)} />
      )}

      <form onSubmit={handleSubmit} className="relative max-w-2xl group" id="audit-form">
        <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-primary-container/20 rounded-2xl blur opacity-25 group-focus-within:opacity-100 transition duration-1000" />
        <div className="relative flex items-center bg-surface-container-lowest p-2 rounded-2xl shadow-xl ring-1 ring-outline-variant/20">
          <span className="material-symbols-outlined ml-4 text-outline">language</span>
          <input
            type="url"
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-hotel-website.com"
            className="w-full bg-transparent border-none focus:ring-0 text-lg px-4 py-4 font-medium placeholder:text-outline/50 text-on-surface"
          />
          <button
            type={user ? "submit" : "button"}
            onClick={!user ? openAuth : undefined}
            disabled={isSubmitting}
            className="bg-primary text-on-primary px-8 py-4 rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-primary/20 transition-all flex items-center gap-2 whitespace-nowrap disabled:opacity-70"
          >
            {isSubmitting ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : !user ? (
              <>
                Log In to Run Audit
                <span className="material-symbols-outlined">login</span>
              </>
            ) : (
              <>
                Run Audit
                <span className="material-symbols-outlined">analytics</span>
              </>
            )}
          </button>
        </div>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-6 text-sm text-on-surface-variant/70 font-label">
        <span className="flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">check_circle</span> Keyword
          Performance
        </span>
        <span className="flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">check_circle</span> Trust Signal
          Analysis
        </span>
        <span className="flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">check_circle</span> Competitor
          Benchmarking
        </span>
      </div>

      {error && errorType === "dns" && (
        <div className="mt-6 p-4 bg-error-container text-on-error-container rounded-xl text-sm">
          {error}
        </div>
      )}
    </>
  );
};

export default AuditInput;
